import { makeStyles, Typography } from '@material-ui/core'
import TableLoadingSkeleton from 'bpm/components/SpecTable/tableBody/TableLoadingSkeleton'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { InView } from 'react-intersection-observer'

export interface InfiniteLoaderProps {
    totalItems?: number
    itemLength?: number
    isLoading?: boolean
    hasNextPage?: boolean
    fetchNextPage?: () => void
    containerWidth?: number
    skeletonItemHeight?: number
}

const useStyles = makeStyles(() => ({
    '@keyframes flickerAnimation': {
        '0%': { opacity: 1 },
        '50%': { opacity: 0 },
        '100%': { opacity: 1 },
    },
    countLoader: {
        animation: 'flickerAnimation 1s infinite',
        display: 'flex',
        justifyContent: 'center',
        position: 'fixed',
        padding: '2rem 0',
    },
}))

const InfiniteLoader: FC<InfiniteLoaderProps> = ({
    isLoading,
    hasNextPage,
    fetchNextPage,
    totalItems = 0,
    itemLength,
    containerWidth,
    skeletonItemHeight = 50,
}) => {
    const classes = useStyles()
    const [isLoaderInView, setIsLoaderInView] = useState(false)

    const loadNext = useCallback((inView) => {
        setIsLoaderInView(inView)
    }, [])

    useEffect(() => {
        if (isLoaderInView && !isLoading && hasNextPage) {
            fetchNextPage?.()
            setIsLoaderInView(false)
        }
    }, [isLoaderInView, isLoading, fetchNextPage])

    return (
        <>
            {!isLoading && (
                <InView
                    as="div"
                    onChange={loadNext}
                    data-testid="infinite-loader"
                >
                    <div
                        style={{
                            height: '1px',
                            width: '100vw',
                        }}
                    ></div>
                </InView>
            )}

            {hasNextPage &&
                (processEnv.REACT_APP_EnableListingCountLoader === '1' ? (
                    <Typography
                        component="div"
                        className={classes.countLoader}
                        style={{
                            width: containerWidth,
                        }}
                    >
                        Loading...
                        {!!totalItems && `${itemLength}/${totalItems}`}
                    </Typography>
                ) : (
                    <TableLoadingSkeleton
                        height={skeletonItemHeight}
                        pageSize={3}
                        data-testid="loader-skeleton"
                        customStyles={{
                            width: containerWidth,
                            display: 'flex',
                            justifyContent: 'center',
                            position: 'fixed',
                            flexDirection: 'column',
                            margin: '0 auto',
                        }}
                    />
                ))}
        </>
    )
}

export default InfiniteLoader
